import React, { FunctionComponent } from 'react';

import cn from 'classnames';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../shared/helpers/i18n';
import {
    getParticipantsCount,
    isSidebarVisible as isSidebarVisibleSelector,
    selectShouldShowNewWaitingRoomPeersIndicator,
} from '../../../../selectors';
import { CollapseBreakpoint } from '../../constants';
import { useDotColor } from '../../hooks/use-dot-color';
import { ToolbarButton } from '../toolbar-button';
import styles from './styles.css';
import { withParticipantsNotifications, WrappingButtonContainerProps } from './with-participants-notifications';

interface ParticipantsCountProps {
    count?: number;
    withDot?: boolean;
}

export const ParticipantsCount: FunctionComponent<ParticipantsCountProps> = ({ count, withDot }) => {
    const hasNewWaitingRoomPeers = useSelector(selectShouldShowNewWaitingRoomPeersIndicator);

    const showDot = withDot && hasNewWaitingRoomPeers;
    return count ? <div className={cn(styles.badge, { [styles.withDot]: showDot })}>{count}</div> : null;
};

type ParticipantsButtonProps = WrappingButtonContainerProps & {
    onClick: () => void;
    disabled?: boolean;
    collapseBreakpoint?: CollapseBreakpoint;
};

const ParticipantsButtonBase: FunctionComponent<ParticipantsButtonProps> = ({
    onClick,
    disabled,
    innerRef,
    onMouseEnter,
    onMouseLeave,
    collapseBreakpoint = CollapseBreakpoint.DEFAULT,
}) => {
    const isSidebarVisible = useSelector(isSidebarVisibleSelector);
    const participantsCount = useSelector(getParticipantsCount);
    const dotColor = useDotColor();

    const buttonClasses = cn(
        styles.Button,
        {
            [styles.isSidebarVisible]: isSidebarVisible,
            [styles.collapsible]: collapseBreakpoint,
        },
        collapseBreakpoint && styles[collapseBreakpoint]
    );

    return (
        <div ref={innerRef}>
            <ToolbarButton
                wrapperProps={{ onMouseEnter, onMouseLeave }}
                className={buttonClasses}
                classNameWrapper={styles.container}
                disabled={disabled}
                onClick={onClick}
                title={i18n('toolbar', 'participants-button-title')}
                testId="participants-button"
                iconName="participants"
                dotType={dotColor}
                content={
                    <div className={styles.textWrapper}>
                        <div className={styles.mainText}>{i18n('toolbar', 'participants-button-title')}</div>
                        <ParticipantsCount count={participantsCount} />
                    </div>
                }
                collapseBreakpoint={null}
            />
        </div>
    );
};

export const ParticipantsButton = withParticipantsNotifications<ParticipantsButtonProps>(ParticipantsButtonBase);
